import React, { useState, useEffect } from 'react';
import {
  AgentDecision,
  DecisionType,
  RiskLevel
} from '../../../types/aiAgent';
import { tone, toneForPriority } from "components/ui/tone";
import { clickable } from "components/ui/interactive";
import { SelectField } from "components/ui/select-field";

interface PendingDecision extends AgentDecision {
  agentName: string;
  taskContext?: {
    templateId?: string;
    testId?: string;
    campaignId?: string;
  };
}

interface AIAgentApprovalWorkflowProps {
  onDecisionApproved?: (decisionId: string, approved: boolean, feedback?: string) => void;
  onBulkAction?: (decisionIds: string[], action: 'approve' | 'reject') => void;
}

export const AIAgentApprovalWorkflow: React.FC<AIAgentApprovalWorkflowProps> = ({
  onDecisionApproved,
  onBulkAction
}) => {
  const [pendingDecisions, setPendingDecisions] = useState<PendingDecision[]>([]);
  const [selectedDecisions, setSelectedDecisions] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [filterBy, setFilterBy] = useState<'all' | DecisionType | RiskLevel>('all');
  const [sortBy, setSortBy] = useState<'timestamp' | 'risk' | 'confidence'>('timestamp');
  const [loading, setLoading] = useState(true);
  const [selectedDecision, setSelectedDecision] = useState<PendingDecision | null>(null);

  useEffect(() => {
    loadPendingDecisions();
  }, []);

  const loadPendingDecisions = async () => {
    try {
      setLoading(true);
      // TODO: Load pending decisions from API
      // const decisions = await aiAgentService.getPendingDecisions();
      setPendingDecisions([]);
    } catch (error) {
      console.error('Failed to load pending decisions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDecisionAction = async (decisionId: string, action: 'approve' | 'reject', feedback?: string) => {
    try {
      // Update decision status
      setPendingDecisions(prev => 
        prev.map(decision => 
          decision.id === decisionId 
            ? { 
                ...decision, 
                approvalStatus: action === 'approve' ? 'approved' : 'rejected',
                approvedAt: new Date(),
                approvedBy: 'current_user' // Would be actual user ID
              }
            : decision
        )
      );

      onDecisionApproved?.(decisionId, action === 'approve', feedback);
    } catch (error) {
      console.error('Failed to process decision:', error);
    }
  };

  const handleBulkAction = async (action: 'approve' | 'reject') => {
    const decisionIds = Array.from(selectedDecisions);
    
    try {
      // Update multiple decisions
      setPendingDecisions(prev => 
        prev.map(decision => 
          decisionIds.includes(decision.id)
            ? { 
                ...decision, 
                approvalStatus: action === 'approve' ? 'approved' : 'rejected',
                approvedAt: new Date(),
                approvedBy: 'current_user'
              }
            : decision
        )
      );

      setSelectedDecisions(new Set());
      onBulkAction?.(decisionIds, action);
    } catch (error) {
      console.error('Failed to process bulk action:', error);
    }
  };

  const toggleDecisionSelection = (decisionId: string) => {
    const newSelection = new Set(selectedDecisions);
    if (newSelection.has(decisionId)) {
      newSelection.delete(decisionId);
    } else {
      newSelection.add(decisionId);
    }
    setSelectedDecisions(newSelection);
  };

  const selectAllDecisions = () => {
    const visibleDecisions = getFilteredDecisions();
    setSelectedDecisions(new Set(visibleDecisions.map(d => d.id)));
  };

  const clearSelection = () => {
    setSelectedDecisions(new Set());
  };

  const getFilteredDecisions = () => {
    let filtered = pendingDecisions.filter(decision => {
      if (activeTab === 'pending') return decision.approvalStatus === 'pending';
      if (activeTab === 'approved') return decision.approvalStatus === 'approved';
      if (activeTab === 'rejected') return decision.approvalStatus === 'rejected';
      return true;
    });

    if (filterBy !== 'all') {
      if (['low', 'medium', 'high', 'critical'].includes(filterBy)) {
        filtered = filtered.filter(d => d.riskLevel === filterBy);
      } else {
        filtered = filtered.filter(d => d.type === filterBy);
      }
    }

    // Sort decisions
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'timestamp':
          return b.timestamp.getTime() - a.timestamp.getTime();
        case 'risk':
          const riskOrder = { low: 1, medium: 2, high: 3, critical: 4 };
          return riskOrder[b.riskLevel] - riskOrder[a.riskLevel];
        case 'confidence':
          return b.confidence - a.confidence;
        default:
          return 0;
      }
    });

    return filtered;
  };

  const _getRiskLevelColor = (riskLevel: RiskLevel) => {
    const colors = {
      low: 'text-green-600 dark:text-green-300 bg-green-100 dark:bg-green-900/30',
      medium: 'text-yellow-600 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30',
      high: 'text-red-600 dark:text-red-300 bg-red-100 dark:bg-red-900/30',
      critical: 'text-red-800 dark:text-red-300 bg-red-200 dark:bg-red-900/40'
    };
    return colors[riskLevel];
  };

  const _getDecisionTypeIcon = (type: DecisionType) => {
    const icons = {
      test_creation: '🧪',
      variant_generation: '🔄',
      winner_implementation: '🏆',
      campaign_optimization: '🚀',
      audience_targeting: '🎯',
      statistical_analysis: '📊',
      content_modification: '✏️',
      resource_allocation: '💰'
    };
    return icons[type] || '⚙️';
  };

  const filteredDecisions = getFilteredDecisions();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg shadow-sm border border-line">
      {/* Header */}
      <div className="border-b border-line px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-content">AI Agent Approval Workflow</h3>
            <p className="text-sm text-content-secondary mt-1">
              Review and approve critical AI agent decisions
            </p>
          </div>
          
          {selectedDecisions.size > 0 && (
            <div className="flex items-center space-x-3">
              <span className="text-sm text-content-secondary">
                {selectedDecisions.size} selected
              </span>
              <button
                onClick={() => handleBulkAction('approve')}
                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
              >
                Approve All
              </button>
              <button
                onClick={() => handleBulkAction('reject')}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
              >
                Reject All
              </button>
              <button
                onClick={clearSelection}
                className="px-3 py-1 border border-line-strong text-gray-700 dark:text-gray-300 text-sm rounded hover:bg-gray-50 dark:hover:bg-dark-700"
              >
                Clear
              </button>
            </div>
          )}
        </div>

        {/* Filters and Controls */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center space-x-4">
            <SelectField
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value as any)}
              className="border border-line-strong rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="winner_implementation">Winner Implementation</option>
              <option value="campaign_optimization">Campaign Optimization</option>
              <option value="resource_allocation">Resource Allocation</option>
              <option value="low">Low Risk</option>
              <option value="medium">Medium Risk</option>
              <option value="high">High Risk</option>
              <option value="critical">Critical Risk</option>
            </SelectField>

            <SelectField
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="border border-line-strong rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="timestamp">Sort by Time</option>
              <option value="risk">Sort by Risk</option>
              <option value="confidence">Sort by Confidence</option>
            </SelectField>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={selectAllDecisions}
              className="text-sm text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-300"
            >
              Select All
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={clearSelection}
              className="text-sm text-content-secondary hover:text-gray-800 dark:hover:text-gray-200"
            >
              Clear All
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-line">
        <nav className="flex space-x-8 px-6">
          {[
            { id: 'pending', label: 'Pending Approval', count: pendingDecisions.filter(d => d.approvalStatus === 'pending').length },
            { id: 'approved', label: 'Approved', count: pendingDecisions.filter(d => d.approvalStatus === 'approved').length },
            { id: 'rejected', label: 'Rejected', count: pendingDecisions.filter(d => d.approvalStatus === 'rejected').length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                  : 'border-transparent text-content-muted hover:text-gray-700 dark:hover:text-gray-300 hover:border-line-strong'
              }`}
            >
              <span>{tab.label}</span>
              {tab.count > 0 && (
                <span className="bg-gray-100 dark:bg-dark-700 text-content-secondary text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Decision List */}
      <div className="divide-y divide-gray-200">
        {filteredDecisions.map(decision => (
          <DecisionCard
            key={decision.id}
            decision={decision}
            isSelected={selectedDecisions.has(decision.id)}
            onToggleSelection={() => toggleDecisionSelection(decision.id)}
            onAction={handleDecisionAction}
            onViewDetails={() => setSelectedDecision(decision)}
          />
        ))}

        {filteredDecisions.length === 0 && (
          <div className="text-center py-12">
            <div className="text-content-subtle text-6xl mb-4">📋</div>
            <h3 className="text-lg font-medium text-content mb-2">
              No {activeTab} decisions
            </h3>
            <p className="text-content-secondary">
              {activeTab === 'pending' 
                ? 'All decisions have been processed' 
                : `No ${activeTab} decisions to show`}
            </p>
          </div>
        )}
      </div>

      {/* Decision Detail Modal */}
      {selectedDecision && (
        <DecisionDetailModal
          decision={selectedDecision}
          onClose={() => setSelectedDecision(null)}
          onAction={handleDecisionAction}
        />
      )}
    </div>
  );
};

// Decision Card Component
const DecisionCard: React.FC<{
  decision: PendingDecision;
  isSelected: boolean;
  onToggleSelection: () => void;
  onAction: (decisionId: string, action: 'approve' | 'reject', feedback?: string) => void;
  onViewDetails: () => void;
}> = ({ decision, isSelected, onToggleSelection, onAction, onViewDetails }) => {
  const getRiskLevelColor = (riskLevel: RiskLevel) => {
    return tone(toneForPriority(riskLevel)).chip;
  };

  const getDecisionTypeIcon = (type: DecisionType) => {
    const icons = {
      test_creation: '🧪',
      variant_generation: '🔄',
      winner_implementation: '🏆',
      campaign_optimization: '🚀',
      audience_targeting: '🎯',
      statistical_analysis: '📊',
      content_modification: '✏️',
      resource_allocation: '💰'
    };
    return icons[type] || '⚙️';
  };

  return (
    <div className={`p-6 hover:bg-gray-50 dark:hover:bg-dark-700 ${isSelected ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}>
      <div className="flex items-start space-x-4">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelection}
          className="mt-1"
        />
        
        <div className="text-2xl">{getDecisionTypeIcon(decision.type)}</div>
        
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <h4 className="text-lg font-medium text-content">
                  {decision.type.replace('_', ' ').toUpperCase()}
                </h4>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskLevelColor(decision.riskLevel)}`}>
                  {decision.riskLevel.toUpperCase()} RISK
                </span>
                <span className="text-sm text-content-secondary">
                  {decision.agentName}
                </span>
              </div>
              
              <p className="text-gray-700 dark:text-gray-300 mb-3">{decision.explanation.summary}</p>
              
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-content-muted">Confidence:</span>
                  <span className="font-medium ml-2">{(decision.confidence * 100).toFixed(1)}%</span>
                </div>
                <div>
                  <span className="text-content-muted">Timestamp:</span>
                  <span className="font-medium ml-2">{decision.timestamp.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-content-muted">Alternatives:</span>
                  <span className="font-medium ml-2">{decision.alternatives.length}</span>
                </div>
              </div>
            </div>
            
            {decision.approvalStatus === 'pending' && (
              <div className="flex space-x-2">
                <button
                  onClick={onViewDetails}
                  className="px-3 py-1 text-sm border border-line-strong rounded hover:bg-gray-50 dark:hover:bg-dark-700"
                >
                  Details
                </button>
                <button
                  onClick={() => onAction(decision.id, 'reject')}
                  className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Reject
                </button>
                <button
                  onClick={() => onAction(decision.id, 'approve')}
                  className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Approve
                </button>
              </div>
            )}
            
            {decision.approvalStatus !== 'pending' && (
              <div className="text-right">
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                  decision.approvalStatus === 'approved' 
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                    : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                }`}>
                  {decision.approvalStatus.toUpperCase()}
                </div>
                {decision.approvedAt && (
                  <div className="text-xs text-content-muted mt-1">
                    {decision.approvedAt.toLocaleString()}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Decision Detail Modal
const DecisionDetailModal: React.FC<{
  decision: PendingDecision;
  onClose: () => void;
  onAction: (decisionId: string, action: 'approve' | 'reject', feedback?: string) => void;
}> = ({ decision, onClose, onAction }) => {
  const [feedback, setFeedback] = useState('');
  const [selectedAlternative, setSelectedAlternative] = useState<string | null>(null);

  const handleAction = (action: 'approve' | 'reject') => {
    onAction(decision.id, action, feedback || undefined);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-surface rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-line flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-content">
              Decision Details - {decision.type.replace('_', ' ').toUpperCase()}
            </h3>
            <p className="text-sm text-content-secondary mt-1">
              Requested by {decision.agentName} • {decision.timestamp.toLocaleString()}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-content-subtle hover:text-content-secondary"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          <div className="space-y-6">
            {/* Summary */}
            <div>
              <h4 className="text-lg font-medium text-content mb-3">Summary</h4>
              <p className="text-gray-700 dark:text-gray-300">{decision.explanation.summary}</p>
            </div>

            {/* Detailed Explanation */}
            <div>
              <h4 className="text-lg font-medium text-content mb-3">Detailed Analysis</h4>
              <p className="text-gray-700 dark:text-gray-300 mb-4">{decision.explanation.details}</p>
              
              {/* Data Points */}
              <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
                <h5 className="font-medium text-content mb-2">Supporting Data</h5>
                <div className="space-y-2">
                  {decision.explanation.dataPoints.map((point, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span className="text-content-secondary capitalize">{point.metric?.replace('_', ' ')}</span>
                      <span className="font-medium">
                        {typeof point.value === 'number' 
                          ? point.value.toFixed(3)
                          : `${point.current} → ${point.projected}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Alternatives */}
            <div>
              <h4 className="text-lg font-medium text-content mb-3">Alternative Approaches</h4>
              <div className="space-y-3">
                {decision.alternatives.map(alt => (
                  <div
                    key={alt.id}
                    className={`border rounded-lg p-4 cursor-pointer ${
                      selectedAlternative === alt.id ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30' : 'border-line'
                    }`}
                    {...clickable(() => setSelectedAlternative(alt.id))}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h5 className="font-medium text-content">{alt.description}</h5>
                      <span className="text-sm text-content-secondary">{(alt.confidence * 100).toFixed(0)}% confidence</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-green-600 dark:text-green-300 font-medium">Pros:</span>
                        <ul className="list-disc list-inside text-content-secondary mt-1">
                          {alt.pros.map((pro, index) => (
                            <li key={index}>{pro}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <span className="text-red-600 dark:text-red-300 font-medium">Cons:</span>
                        <ul className="list-disc list-inside text-content-secondary mt-1">
                          {alt.cons.map((con, index) => (
                            <li key={index}>{con}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Assumptions */}
            <div>
              <h4 className="text-lg font-medium text-content mb-3">Key Assumptions</h4>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
                {decision.explanation.assumptions.map((assumption, index) => (
                  <li key={index}>{assumption}</li>
                ))}
              </ul>
            </div>

            {/* Feedback */}
            <div>
              <h4 className="text-lg font-medium text-content mb-3">Feedback (Optional)</h4>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Provide feedback for the AI agent..."
                rows={3}
                className="w-full border border-line-strong rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-line flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-line-strong rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
          >
            Cancel
          </button>
          <button
            onClick={() => handleAction('reject')}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Reject Decision
          </button>
          <button
            onClick={() => handleAction('approve')}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Approve Decision
          </button>
        </div>
      </div>
    </div>
  );
};